import { useState, useRef, useEffect, useMemo } from 'react'
import { clsx } from 'clsx'

interface SymbolSearchProps {
  currentSymbol: string
  onSymbolChange: (symbol: string) => void
}

// Popular symbols
const popularSymbols = [
  // Crypto
  { symbol: 'BTC/USDT', name: 'Bitcoin', type: 'crypto' },
  { symbol: 'ETH/USDT', name: 'Ethereum', type: 'crypto' },
  { symbol: 'SOL/USDT', name: 'Solana', type: 'crypto' },
  { symbol: 'BNB/USDT', name: 'Binance Coin', type: 'crypto' },
  { symbol: 'XRP/USDT', name: 'Ripple', type: 'crypto' },
  { symbol: 'ADA/USDT', name: 'Cardano', type: 'crypto' },
  { symbol: 'DOGE/USDT', name: 'Dogecoin', type: 'crypto' },
  { symbol: 'DOT/USDT', name: 'Polkadot', type: 'crypto' },
  { symbol: 'AVAX/USDT', name: 'Avalanche', type: 'crypto' },
  { symbol: 'MATIC/USDT', name: 'Polygon', type: 'crypto' },
  // Forex
  { symbol: 'EUR/USD', name: 'Euro/US Dollar', type: 'forex' },
  { symbol: 'GBP/USD', name: 'British Pound/US Dollar', type: 'forex' },
  { symbol: 'USD/JPY', name: 'US Dollar/Japanese Yen', type: 'forex' },
  { symbol: 'USD/CHF', name: 'US Dollar/Swiss Franc', type: 'forex' },
  { symbol: 'AUD/USD', name: 'Australian Dollar/US Dollar', type: 'forex' },
  { symbol: 'EUR/GBP', name: 'Euro/British Pound', type: 'forex' },
  { symbol: 'EUR/JPY', name: 'Euro/Japanese Yen', type: 'forex' },
  // Stocks
  { symbol: 'AAPL', name: 'Apple Inc.', type: 'stock' },
  { symbol: 'GOOGL', name: 'Alphabet Inc.', type: 'stock' },
  { symbol: 'MSFT', name: 'Microsoft Corporation', type: 'stock' },
  { symbol: 'AMZN', name: 'Amazon.com Inc.', type: 'stock' },
  { symbol: 'TSLA', name: 'Tesla Inc.', type: 'stock' },
  { symbol: 'NVDA', name: 'NVIDIA Corporation', type: 'stock' },
  { symbol: 'META', name: 'Meta Platforms Inc.', type: 'stock' },
  // Indices
  { symbol: 'SPX', name: 'S&P 500', type: 'index' },
  { symbol: 'NDX', name: 'NASDAQ 100', type: 'index' },
  { symbol: 'DJI', name: 'Dow Jones Industrial', type: 'index' },
  // Commodities
  { symbol: 'XAUUSD', name: 'Gold', type: 'commodity' },
  { symbol: 'XAGUSD', name: 'Silver', type: 'commodity' },
  { symbol: 'WTIUSD', name: 'Crude Oil WTI', type: 'commodity' },
]

const typeIcons: Record<string, string> = {
  crypto: '₿',
  forex: '💱',
  stock: '📈',
  index: '📊',
  commodity: '🪙',
}

export function SymbolSearch({ currentSymbol, onSymbolChange }: SymbolSearchProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  const filteredSymbols = useMemo(() => {
    if (!search) return popularSymbols
    const lower = search.toLowerCase()
    return popularSymbols.filter(
      s => s.symbol.toLowerCase().includes(lower) || s.name.toLowerCase().includes(lower)
    )
  }, [search])

  const groupedSymbols = useMemo(() => {
    const groups: Record<string, typeof popularSymbols> = {}
    filteredSymbols.forEach(s => {
      if (!groups[s.type]) groups[s.type] = []
      groups[s.type].push(s)
    })
    return groups
  }, [filteredSymbols])

  const handleSelect = (symbol: string) => {
    onSymbolChange(symbol)
    setIsOpen(false)
    setSearch('')
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-kit-darker rounded-lg hover:bg-kit-card transition-colors min-w-[180px]"
      >
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <span className="font-bold text-white">{currentSymbol}</span>
        <svg className="w-4 h-4 text-gray-400 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 left-0 bg-kit-card border border-kit-border rounded-lg shadow-xl z-50 w-[320px] max-h-[400px] overflow-hidden">
          {/* Search Input */}
          <div className="p-3 border-b border-kit-border">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search symbol or name..."
              className="w-full px-3 py-2 bg-kit-darker border border-kit-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-kit-accent"
            />
          </div>

          {/* Symbol List */}
          <div className="overflow-y-auto max-h-[320px]">
            {Object.entries(groupedSymbols).map(([type, symbols]) => (
              <div key={type}>
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase bg-kit-darker sticky top-0">
                  {typeIcons[type]} {type}
                </div>
                {symbols.map(({ symbol, name }) => (
                  <button
                    key={symbol}
                    onClick={() => handleSelect(symbol)}
                    className={clsx(
                      'w-full px-3 py-2 flex items-center justify-between hover:bg-kit-darker transition-colors',
                      currentSymbol === symbol && 'bg-kit-accent/10'
                    )}
                  >
                    <div className="flex flex-col items-start">
                      <span className="font-medium text-white">{symbol}</span>
                      <span className="text-xs text-gray-500">{name}</span>
                    </div>
                    {currentSymbol === symbol && (
                      <svg className="w-4 h-4 text-kit-accent" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            ))}

            {filteredSymbols.length === 0 && (
              <div className="p-4 text-center text-gray-500">
                No symbols found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
